// Google Ads Automation Service
import { ClaudeAdWriter } from './claudeAdWriter';
import { googleAdsService } from './googleAdsService';
import { AdCampaign, AdGenerationRequest, CampaignOptimization } from '../types/googleAds.types';

export class GoogleAdsAutomation {
  private adWriter: ClaudeAdWriter;
  private isRunning: boolean = false;
  
  constructor(apiKey: string) {
    this.adWriter = new ClaudeAdWriter(apiKey);
  }

  // Uruchom automatyczną optymalizację
  async startOptimization() {
    if (this.isRunning) return;
    this.isRunning = true;
    
    console.log('🎯 Automatyczna optymalizacja Google Ads uruchomiona');
    
    // Sprawdzaj codziennie o 9:00
    const checkDaily = () => {
      const now = new Date();
      const next9am = new Date();
      next9am.setHours(9, 0, 0, 0);
      
      if (now > next9am) {
        next9am.setDate(next9am.getDate() + 1);
      }
      
      const timeout = next9am.getTime() - now.getTime();
      
      setTimeout(() => {
        this.runDailyOptimization();
        checkDaily(); // Zaplanuj następny
      }, timeout);
    };
    
    checkDaily();
    
    // Pierwsza optymalizacja
    this.runDailyOptimization();
  }

  // Codzienna optymalizacja
  private async runDailyOptimization() {
    try {
      console.log('🔄 Rozpoczynam codzienną optymalizację...');
      
      // 1. Pobierz aktywne kampanie
      const campaigns = await googleAdsService.getCampaigns({ status: 'active' });
      
      for (const campaign of campaigns) {
        // 2. Analizuj wydajność
        const optimization = await this.analyzeCampaignPerformance(campaign);
        
        // 3. Automatycznie zastosuj optymalizacje
        await this.applyOptimizations(campaign, optimization);
        
        // 4. Generuj nowe warianty reklam dla słabo działających
        await this.optimizePoorPerformingAds(campaign);
      }
      
      // 5. Generuj raport
      const report = await this.generateDailyReport(campaigns);
      console.log('📊 Raport dzienny:', report);
      
    } catch (error) {
      console.error('❌ Błąd optymalizacji:', error);
    }
  }

  // Analizuj wydajność kampanii
  private async analyzeCampaignPerformance(campaign: AdCampaign): Promise<CampaignOptimization> {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    
    const stats = await googleAdsService.getCampaignStats(campaign.id, startDate, endDate);
    
    const optimization = await googleAdsService.getCampaignOptimizations(campaign.id);
    
    // Dodaj własne rekomendacje AI
    const aiRecommendations = await this.generateAIRecommendations(campaign, stats);
    
    return {
      ...optimization,
      recommendations: [...optimization.recommendations, ...aiRecommendations]
    };
  }

  // Generuj rekomendacje AI
  private async generateAIRecommendations(campaign: AdCampaign, stats: any[]): Promise<any[]> {
    const recommendations = [];
    
    // Analiza CTR
    const avgCtr = stats.reduce((sum, s) => sum + s.ctr, 0) / stats.length;
    if (avgCtr < 2) {
      recommendations.push({
        type: 'ads',
        priority: 'high',
        description: 'Niski CTR - wygeneruj nowe warianty reklam',
        estimated_impact: '+50% CTR',
        action_items: [
          'Przetestuj bardziej emocjonalne nagłówki',
          'Dodaj liczby i konkretne korzyści',
          'Użyj słów akcji w opisach'
        ]
      });
    }
    
    // Analiza kosztów
    const totalCost = stats.reduce((sum, s) => sum + s.cost, 0);
    const avgCpc = totalCost / stats.reduce((sum, s) => sum + s.clicks, 0);
    if (avgCpc > 5) {
      recommendations.push({
        type: 'keywords',
        priority: 'medium',
        description: 'Wysoki CPC - optymalizuj słowa kluczowe',
        estimated_impact: '-30% kosztów',
        action_items: [
          'Dodaj wykluczające słowa kluczowe',
          'Zmień dopasowania na dokładniejsze',
          'Usuń słowa o niskim Quality Score'
        ]
      });
    }
    
    return recommendations;
  }

  // Zastosuj optymalizacje
  private async applyOptimizations(campaign: AdCampaign, optimization: CampaignOptimization) {
    for (const rec of optimization.recommendations) {
      if (rec.priority === 'high') {
        switch (rec.type) {
          case 'budget':
            // Automatycznie zwiększ budżet o 20%
            if (campaign.budget_daily < 100) {
              await googleAdsService.updateCampaign(campaign.id, {
                budget_daily: campaign.budget_daily * 1.2
              });
              console.log(`📈 Zwiększono budżet kampanii ${campaign.name} o 20%`);
            }
            break;
            
          case 'ads':
            // Wygeneruj nowe reklamy
            await this.generateNewAdVariants(campaign.id);
            break;
        }
      }
    }
  }

  // Optymalizuj słabo działające reklamy
  private async optimizePoorPerformingAds(campaign: AdCampaign) {
    const adGroups = await googleAdsService.getAdGroups(campaign.id);
    
    for (const adGroup of adGroups) {
      const ads = await googleAdsService.getAds(adGroup.id);
      
      // Znajdź słabo działające reklamy (symulacja)
      const poorPerformingAds = ads.filter(ad => {
        // W rzeczywistości sprawdzalibyśmy rzeczywiste statystyki
        return Math.random() < 0.3; // 30% reklam "źle działa"
      });
      
      for (const ad of poorPerformingAds) {
        // Wygeneruj lepszą wersję
        const optimized = await this.adWriter.optimizeAdCopy({
          headlines: [ad.headline1, ad.headline2, ad.headline3 || ''],
          descriptions: [ad.description1, ad.description2 || ''],
          performance: { ctr: 1.5, conversions: 2 } // Symulowane dane
        });
        
        // Stwórz nową reklamę
        await googleAdsService.createAd({
          ad_group_id: adGroup.id,
          headline1: optimized.optimized_headlines[0],
          headline2: optimized.optimized_headlines[1],
          headline3: optimized.optimized_headlines[2],
          description1: optimized.optimized_descriptions[0],
          description2: optimized.optimized_descriptions[1],
          final_url: ad.final_url,
          path1: ad.path1,
          path2: ad.path2,
          ai_generated: true,
          status: 'draft'
        });
        
        console.log(`🔄 Zoptymalizowano reklamę w grupie ${adGroup.name}`);
      }
    }
  }

  // Generuj nowe warianty reklam
  private async generateNewAdVariants(campaignId: string) {
    const adGroups = await googleAdsService.getAdGroups(campaignId);
    
    for (const adGroup of adGroups) {
      const keywords = await googleAdsService.getKeywords(adGroup.id);
      
      if (keywords.length === 0) continue;
      
      // Generuj nowe reklamy na podstawie słów kluczowych
      const request: AdGenerationRequest = {
        product_name: 'Ekspozytory z plexi',
        product_description: 'Profesjonalne ekspozytory reklamowe z pleksi na zamówienie',
        keywords: keywords.map(k => k.keyword).slice(0, 5),
        tone: 'professional',
        include_price: true,
        price: 'od 99 zł'
      };
      
      const generated = await this.adWriter.generateAds(request);
      
      // Stwórz 2 nowe warianty
      for (let i = 0; i < 2; i++) {
        await googleAdsService.createAd({
          ad_group_id: adGroup.id,
          headline1: generated.headlines[i * 3],
          headline2: generated.headlines[i * 3 + 1],
          headline3: generated.headlines[i * 3 + 2],
          description1: generated.descriptions[i * 2],
          description2: generated.descriptions[i * 2 + 1],
          final_url: 'https://plexisystem.pl/ekspozytory',
          path1: 'ekspozytory',
          path2: 'plexi',
          ai_generated: true,
          status: 'draft'
        });
      }
      
      console.log(`✨ Wygenerowano 2 nowe warianty reklam dla grupy ${adGroup.name}`);
    }
  }

  // Generuj dzienny raport
  private async generateDailyReport(campaigns: AdCampaign[]): Promise<string> {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 1);
    
    const stats = await googleAdsService.getAggregatedStats(
      campaigns.map(c => c.id),
      startDate,
      endDate
    );
    
    const report = `
🎯 DZIENNY RAPORT GOOGLE ADS
============================

📅 Data: ${endDate.toLocaleDateString('pl-PL')}

WYDAJNOŚĆ:
• Wyświetlenia: ${stats.totalImpressions.toLocaleString('pl-PL')}
• Kliknięcia: ${stats.totalClicks.toLocaleString('pl-PL')}
• CTR: ${stats.avgCtr.toFixed(2)}%
• Średni CPC: ${stats.avgCpc.toFixed(2)} PLN
• Koszt: ${stats.totalCost.toFixed(2)} PLN
• Konwersje: ${stats.totalConversions}
• ROAS: ${stats.roas.toFixed(2)}x

OPTYMALIZACJE WYKONANE DZIŚ:
${campaigns.map(c => `• ${c.name}: budżet ${c.budget_daily} PLN/dzień`).join('\n')}

REKOMENDACJE NA JUTRO:
${stats.avgCtr < 2 ? '⚠️ Popraw CTR - testuj nowe teksty reklam' : '✅ CTR w normie'}
${stats.avgCpc > 5 ? '⚠️ Obniż CPC - zoptymalizuj słowa kluczowe' : '✅ CPC w normie'}
${stats.roas < 3 ? '⚠️ Zwiększ ROAS - popraw strony docelowe' : '✅ ROAS w normie'}

--
Automatyczna optymalizacja PlexiSystem AI
    `;
    
    return report;
  }

  // Automatyczne badanie konkurencji
  async analyzeCompetition(competitorDomains: string[]): Promise<void> {
    console.log('🔍 Analizuję konkurencję...');
    
    for (const domain of competitorDomains) {
      const insights = await this.adWriter.analyzeCompetitorAds(
        domain,
        ['plexi na wymiar', 'ekspozytory', 'gabloty']
      );
      
      console.log(`\nAnaliza ${domain}:`);
      console.log('Spostrzeżenia:', insights.insights);
      console.log('Rekomendacje:', insights.recommendations);
    }
  }
}

// Singleton dla automatyzacji
let automationInstance: GoogleAdsAutomation | null = null;

export const startGoogleAdsAutomation = (apiKey: string) => {
  if (!automationInstance) {
    automationInstance = new GoogleAdsAutomation(apiKey);
  }
  automationInstance.startOptimization();
  return automationInstance;
};

export const getGoogleAdsAutomation = () => automationInstance;
